import * as z from 'zod'
import { NavigationSettingsFormValues } from './types'
import {
  buildLinkTargetVariables,
  linkTargetRefine,
  linkTargetSchemaFields,
} from '@/lib/linkTarget'

const navItemProfileSchema = z.object({
  language: z.string(),
  label: z.string(),
})

const navDropdownLinkProfileSchema = z.object({
  language: z.string(),
  label: z.string(),
})

const navDropdownLinkSchema = z.object({
  _key: z.string(),
  ...linkTargetSchemaFields,
  profiles: z.array(navDropdownLinkProfileSchema),
})

const navItemSchema = z.object({
  _key: z.string(),
  type: z.enum(['link', 'dropdown', 'mega_menu']),
  ...linkTargetSchemaFields,
  panel_root_category_id: z.number().nullable(),
  panel_root_page_id: z.string().nullable(),
  panel_source: z.enum(['categories', 'page', 'manual']),
  profiles: z.array(navItemProfileSchema),
  featured_page_id: z.string().nullable(),
  links: z.array(navDropdownLinkSchema),
})

/**
 * Per-link validation shared by `dropdown` items and manual-source
 * `mega_menu` panels. Both edit the same `LinkRowFormValues` rows through the
 * same `LinkRowEditor`, so they must reject exactly the same things: an empty
 * list, a row with no label in any language, and a row with no resolvable
 * target.
 */
function refineLinkRows(
  links: NavigationSettingsFormValues['items'][number]['links'],
  ctx: z.RefinementCtx,
  itemIndex: number,
  activeLanguageId: number
) {
  if (links.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Add at least one link',
      path: ['items', itemIndex, 'links'],
    })
  }

  links.forEach((link, linkIndex) => {
    const hasLinkLabel = link.profiles.some(p => p.label?.trim())
    if (!hasLinkLabel) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one language needs a label',
        path: [
          'items',
          itemIndex,
          'links',
          linkIndex,
          'profiles',
          activeLanguageId,
          'label',
        ],
      })
    }

    linkTargetRefine(link, ctx, ['items', itemIndex, 'links', linkIndex])
  })
}

/**
 * Validation ONLY — no `.transform()`. `items[].profiles` is addressed
 * positionally by `languageId` (`items.${index}.profiles.${languageId}.label`),
 * so a transform that reshapes/filters it here would desync the resolver's
 * parsed output from the form's field paths — the same corruption class
 * `contentBlockFormSchema`'s doc comment warns about. Every seeded profile
 * row (including empty labels) is sent as-is by `buildNavigationVariables`.
 *
 * A FACTORY, not a static schema: the "needs a label" issue is attached to
 * `profiles.${activeLanguageId}.label` — the ONE profile index the form
 * actually renders a field (and `FormMessage`) for at any given time (see
 * `NavigationItemEditor`, keyed off `LanguageContext`'s positional
 * `languageId`). Hardcoding index 0 would attach the inline error to a field
 * that's off-screen whenever the admin's active language isn't the first
 * one. The container recomputes the resolver (`zodResolver(...)`) whenever
 * `activeLanguageId` changes — react-hook-form re-reads `resolver` off its
 * own `_options` on every render, so this stays in sync.
 */
export function buildNavigationSettingsFormSchema(activeLanguageId: number) {
  return z
    .object({
      items: z.array(navItemSchema),
    })
    .superRefine((data, ctx) => {
      data.items.forEach((item, index) => {
        const hasLabel = item.profiles.some(p => p.label?.trim())
        if (!hasLabel) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language needs a label',
            path: ['items', index, 'profiles', activeLanguageId, 'label'],
          })
        }

        if (item.type === 'link') {
          linkTargetRefine(item, ctx, ['items', index])
        }

        // A dropdown item's trigger has no target of its own (it just opens
        // the list below) — the curated links are what needs at least one
        // entry, each with its own label and target.
        if (item.type === 'dropdown') {
          refineLinkRows(item.links, ctx, index, activeLanguageId)
        }

        // A mega_menu item sourced from a CMS page needs that page picked —
        // an empty picker would silently save `panel_root_page_id: null`
        // (no scoping at all) instead of the "child pages of X" panel the
        // admin chose "CMS page" for. Attached to `panel_root_page_id`, the
        // one field the picker's `InputFieldContainer`/`FormMessage` renders.
        if (
          item.type === 'mega_menu' &&
          item.panel_source === 'page' &&
          !item.panel_root_page_id
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Select a page',
            path: ['items', index, 'panel_root_page_id'],
          })
        }

        // A manual panel's columns ARE its links: with none, the portal has
        // no category tree to fall back to and the panel collapses to a bare
        // label. Rejected exactly like an empty dropdown.
        if (item.type === 'mega_menu' && item.panel_source === 'manual') {
          refineLinkRows(item.links, ctx, index, activeLanguageId)
        }
      })
    }) satisfies z.ZodType<NavigationSettingsFormValues>
}

/**
 * Build the `updateNavigationSettings` mutation variables from validated
 * form values. PURE — no network calls. Always sends the FULL ordered
 * array (order = array position, there is no `arrangement` input field).
 */
export function buildNavigationVariables(values: NavigationSettingsFormValues) {
  return {
    items: values.items.map(item => {
      const isMegaMenu = item.type === 'mega_menu'
      const isDropdown = item.type === 'dropdown'
      // A manual panel is the second producer of `links` — see `refineLinkRows`.
      const isManualPanel = isMegaMenu && item.panel_source === 'manual'
      return {
        type: item.type,
        ...buildLinkTargetVariables(item),
        // Only meaningful for mega_menu; non-mega items are stored with the
        // `categories` default server-side either way, and sending it
        // explicitly keeps the payload readable.
        panel_source: isMegaMenu ? item.panel_source : 'categories',
        // Only the scope for whichever source is selected — the backend nulls
        // the others server-side (see `resolveNavPanelWrite`), but gating here
        // too keeps the admin's own payload honest instead of depending on
        // that fallback.
        panel_root_category_id:
          isMegaMenu && item.panel_source === 'categories'
            ? item.panel_root_category_id
            : null,
        panel_root_page_id:
          isMegaMenu && item.panel_source === 'page'
            ? item.panel_root_page_id
            : null,
        profiles: item.profiles.map(p => ({
          language: p.language,
          label: p.label,
        })),
        // Only meaningful for mega_menu, persisted only for that type like
        // `panel_root_*` above.
        featured_page_id: isMegaMenu ? item.featured_page_id : null,
        // Sent for dropdowns and for manual panels; dropped for every other
        // combination so a stale array from a prior type switch can never be
        // persisted — order = array position, there is no `arrangement` input.
        links:
          isDropdown || isManualPanel
            ? item.links.map(link => ({
                ...buildLinkTargetVariables(link),
                profiles: link.profiles.map(p => ({
                  language: p.language,
                  label: p.label,
                })),
              }))
            : null,
      }
    }),
  }
}
